import React, {useContext, useMemo} from 'react';
import {DragDropContext, Droppable} from 'react-beautiful-dnd';
import {useDispatch, useSelector} from 'react-redux';
import IphoneFrame from '../../../../../../assets/icons/iPhone_X.svg?react';
import Elements from '../../../../../components/slides/form/elements/Elements';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {SlideRefContext} from '../../../../../context/SlideRefContext';
import {useSlideBackground} from '../../../../../hooks/useSlideBackground';
import {setSidePanelContentAction} from '../../../../../store/actions/grid';
import {setHighlightedElementIndex} from '../../../../../store/actions/slide';
import {SidePanelContent} from '../../../../../store/actions/types/grid.types';
import {selectedSlideSelector} from '../../../../../store/selectors/slide.selectors';
import {useSlideClickHandler} from '../../../../../utils/hooks/useHandleSlideClick';
import {useSortMobileElements} from '../../../../../utils/hooks/useSortMobileElements';
import {useSlideUpdateContext} from '../../../../context/SetSlideUpdateContext';
import usePresScale from '../../../../util/usePresScale';
import MobileStatusBar from './MobileStatusBar';
import MobileViewFooter from './MobileViewFooter';
import MobileViewHeader from './MobileViewHeader';
import {presUpdateSpinnerSelector} from '../../../../../store/selectors/spinner.selector';

export const SLIDE_WORKSPACE_MOBILE = 'slide-workspace-mobile';

function SlideContainerMobile(): JSX.Element {
    const dispatch = useDispatch();
    const {slideRef} = useContext(SlideRefContext);

    const slide = useSelector(selectedSlideSelector);
    const presUpdateSpinner = useSelector(presUpdateSpinnerSelector);
    const {setSlide, cancelAutosave} = useSlideUpdateContext();
    const {scale} = usePresScale();
    const sortedMobileElements = useSortMobileElements();
    const handleSlideClick = useSlideClickHandler();
    const {effectiveBackgroundColor, effectiveBackgroundImage} = useSlideBackground();

    function changeOrder(currentOrder: number, newOrder: number): void {
        const {elements} = slide;
        const reorderedMobileElements = [...sortedMobileElements];
        const [objectToMove] = reorderedMobileElements.splice(currentOrder, 1);
        if (!objectToMove || currentOrder === newOrder) {
            return;
        }

        reorderedMobileElements.splice(newOrder, 0, objectToMove);
        const orderMap = new Map(reorderedMobileElements.map((element, index) => [element.id, index]));

        const updatedElements = elements.map((element) => ({
            ...element,
            mobileOrder: orderMap.get(element.id),
        }));

        setSlide({...slide, elements: updatedElements});
    }

    return (
        <div key={String(presUpdateSpinner)} className="p-presEdit__slideMobile">
            <IphoneFrame />

            <div
                id={SLIDE_WORKSPACE_MOBILE}
                ref={slideRef}
                style={{
                    fontSize: `${17 * scale}px`,
                    borderRadius: `${30 * scale}px`,
                    backgroundColor: effectiveBackgroundColor,
                    backgroundImage: `url('${effectiveBackgroundImage}')`,
                    backgroundSize: '100% 100%',
                }}
                onClick={(e) => handleSlideClick(e, SLIDE_WORKSPACE_MOBILE)}
            >
                <MobileViewHeader />

                <div className="p-presEdit__mobileContent">
                    <MobileStatusBar />

                    <DragDropContext
                        onDragStart={cancelAutosave}
                        onDragEnd={(e) => {
                            if (typeof e.destination?.index !== 'undefined') changeOrder(e.source.index, e.destination.index);
                        }}
                    >
                        <Droppable droppableId={slide._id}>
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    <Elements
                                        device={DeviceType.MOBILE}
                                        slideId={slide._id}
                                        elements={sortedMobileElements || []}
                                        onOptions={(id: string, silent?: boolean) => {
                                            const index = slide.elements.findIndex((el) => el.id === id);
                                            dispatch(setHighlightedElementIndex(index));

                                            if (!silent) {
                                                dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
                                            }
                                        }}
                                    />

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

                <MobileViewFooter />
            </div>
        </div>
    );
}

export default SlideContainerMobile;
